import React, { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, deleteField, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { FiAlertCircle, FiCalendar, FiEdit2, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { useSessionUser } from '../../lib/permissions';
import { formatDate } from '../../lib/format';
import { archiveDocuments, documentError, sendFiles } from '../../lib/documents';
import { BUTTON, CARD, EmptyState, Field, INPUT, Modal, Notice, PageHeader, Pill, Segmented, Select, SIZE, SkeletonRows, TextInput } from '../ui/kit';
import { UNITS } from '../patients/patientModel';
import AttachmentsField, { useDocumentsStatus } from '../documents/AttachmentsField';
import DocumentLinks from '../documents/DocumentLinks';
import DocumentViewer from '../documents/DocumentViewer';
import { dayOf, documentsOf, legacyLinks, periodLabel, periodOf, scheduleAccess, titleOf, todayKey } from './scheduleModel';

// A list of documents that each cover a period (operative programme, duty rosters), sorted by
// date: upcoming, past, and the former documents without a date. `config` gives the Firestore
// collection, the rights section and the words of the page (see ProgrammePage, GardesPage).
const PAGE_SIZE = 30;
const VIEWS = [
  { value: 'upcoming', label: 'À venir' },
  { value: 'past', label: 'Passés' },
  { value: 'undated', label: 'Sans date' },
];

const emptyForm = (unit, types) => ({
  title: '',
  start: todayKey(),
  end: todayKey(),
  type: types ? types.options[0].value : '',
  comment: '',
  unit,
  documents: [],
  links: [],
  pending: [],
  removed: [],
});

const DateBadge = ({ period, current, tint }) => {
  const day = period ? dayOf(period.start) : null;
  return (
    <span className={`flex h-12 w-12 flex-none flex-col items-center justify-center rounded-2xl ${current ? tint.current : tint.idle}`} aria-hidden="true">
      {day ? (
        <>
          <span className="text-[17px] font-semibold leading-none">{day.getDate()}</span>
          <span className="mt-0.5 text-[10.5px] font-semibold uppercase leading-none tracking-wide">{day.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '')}</span>
        </>
      ) : (
        <FiCalendar size={18} />
      )}
    </span>
  );
};

const ScheduleRow = memo(({ entry, types, tint, editable, deletable, onOpen, onEdit, onDelete }) => {
  const { item, period, current } = entry;
  return (
    <li className="flex items-start gap-[14px] border-0 border-b-[1px] border-solid border-black/[0.05] px-[16px] py-[16px] last:border-b-0 sm:px-6">
      <DateBadge period={period} current={current} tint={tint} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="m-0 break-words text-[15px] font-semibold text-ink">{entry.title}</p>
          {current && <Pill tone="green">En cours</Pill>}
        </div>
        <p className="mb-0 mt-0.5 text-[13px] text-black/50">
          {entry.periodText}
          {!period && entry.addedText && <span className="text-black/40"> · déposé le {entry.addedText}</span>}
        </p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {types && <Pill tone={entry.type === types.options[0].value ? 'neutral' : 'brand'}>{types.label(item)}</Pill>}
          {item.service ? <Pill>{item.service}</Pill> : <Pill tone="slate">Toutes unités</Pill>}
        </div>
        {(entry.documents.length > 0 || entry.links.length > 0) && (
          <div className="mt-2">
            <DocumentLinks documents={entry.documents} links={entry.links} onOpen={(document) => onOpen({ document, item })} size="sm" />
          </div>
        )}
      </div>
      {(editable || deletable) && (
        <div className="-mr-1 flex flex-none items-center gap-1">
          {editable && (
            <button type="button" onClick={() => onEdit(item)} aria-label={`Modifier ${entry.title}`} title="Modifier" className={`${BUTTON.ghost} h-9 w-9 p-0 text-black/50`}>
              <FiEdit2 size={15} aria-hidden="true" />
            </button>
          )}
          {deletable && (
            <button type="button" onClick={() => onDelete(item)} aria-label={`Supprimer ${entry.title}`} title="Supprimer" className={`${BUTTON.ghost} h-9 w-9 p-0 text-red-500 hover:bg-red-50 hover:text-red-600`}>
              <FiTrash2 size={15} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </li>
  );
});

const SchedulePage = ({ config }) => {
  const { words, types } = config;
  const user = useSessionUser();
  const access = scheduleAccess(user, config.section);
  const documentsStatus = useDocumentsStatus();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('upcoming');
  const [viewChosen, setViewChosen] = useState(false);
  const [unit, setUnit] = useState(null); // null: the user's unit when present
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [dialog, setDialog] = useState(null); // { mode: 'form' | 'delete', item? }
  const [form, setForm] = useState(() => emptyForm('', types));
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null);
  const [dialogError, setDialogError] = useState('');
  const [notice, setNotice] = useState('');
  const [viewing, setViewing] = useState(null); // { document, item }
  const plural = (count) => (count > 1 ? words.many : words.one);

  useEffect(
    () =>
      onSnapshot(
        collection(db, config.collection),
        (snapshot) => {
          setItems(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
          setStatus('ready');
        },
        () => setStatus('error')
      ),
    [config.collection]
  );

  const today = todayKey();
  // Period, title and search text computed once per change of the data.
  const prepared = useMemo(
    () =>
      items.map((item) => {
        const period = periodOf(item);
        const documents = documentsOf(item);
        const links = legacyLinks(item);
        const title = titleOf(item, words.fallbackName);
        const periodText = periodLabel(period);
        const added = item.createdAt || item.timestamp || '';
        return {
          item,
          period,
          current: Boolean(period && period.start <= today && today <= period.end),
          title,
          periodText,
          added,
          addedText: added ? formatDate(added) : '',
          type: types ? types.of(item) : '',
          documents,
          links,
          search: [title, types && types.label(item), item.service, periodText, ...documents.map((d) => d.name), ...links.map((l) => l.name)].join(' ').toLowerCase(),
        };
      }),
    [items, today, types, words.fallbackName]
  );

  const unitsInData = useMemo(() => [...new Set(items.map((item) => item.service).filter(Boolean))].sort(), [items]);
  const defaultUnit = unitsInData.find((name) => access.myUnits.includes(name.toLowerCase())) || '';
  const activeUnit = unit === null ? defaultUnit : unit;

  const query = useDeferredValue(search.trim().toLowerCase());
  const matching = useMemo(
    () =>
      prepared.filter(
        (entry) =>
          // Former entries have no unit: they concern every unit.
          (!activeUnit || !entry.item.service || entry.item.service === activeUnit) && (!query || query.split(/\s+/).every((word) => entry.search.includes(word)))
      ),
    [prepared, activeUnit, query]
  );
  const counts = useMemo(
    () => ({
      upcoming: matching.filter((entry) => entry.period && entry.period.end >= today).length,
      past: matching.filter((entry) => entry.period && entry.period.end < today).length,
      undated: matching.filter((entry) => !entry.period).length,
    }),
    [matching, today]
  );
  const shown = useMemo(() => {
    if (view === 'upcoming') return matching.filter((entry) => entry.period && entry.period.end >= today).sort((a, b) => a.period.start.localeCompare(b.period.start));
    if (view === 'past') return matching.filter((entry) => entry.period && entry.period.end < today).sort((a, b) => b.period.start.localeCompare(a.period.start));
    // Former documents: the latest deposited first, then by title.
    return matching.filter((entry) => !entry.period).sort((a, b) => String(b.added).localeCompare(String(a.added)) || a.title.localeCompare(b.title, 'fr'));
  }, [matching, view, today]);

  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [view, activeUnit, query]);

  // Until dates are given to the former documents, open on them rather than on an empty list.
  useEffect(() => {
    if (status === 'ready' && !viewChosen && view === 'upcoming' && !counts.upcoming && !counts.past && counts.undated) setView('undated');
  }, [status, viewChosen, view, counts]);
  const chooseView = (value) => {
    setViewChosen(true);
    setView(value);
  };

  const unitChoices = access.isAdmin ? UNITS : access.myUnits.length > 1 ? UNITS.filter((name) => access.myUnits.includes(name.toLowerCase())) : [];

  const openAdd = () => {
    setForm(emptyForm(unitChoices.length ? '' : user.service || '', types));
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'form' });
  };
  const openEdit = useCallback(
    (item) => {
      const period = periodOf(item);
      const type = types ? types.of(item) : '';
      setForm({
        title: String(item.title || '').trim() || (item.link ? '' : item.name || ''),
        start: period ? period.start : '',
        end: period ? period.end : '',
        type,
        comment: types && type === 'Autres' ? String(item.comment || '').trim() : '',
        unit: item.service || '',
        documents: documentsOf(item),
        links: legacyLinks(item),
        pending: [],
        removed: [],
      });
      setDialogError('');
      setNotice('');
      setDialog({ mode: 'form', item });
    },
    [types]
  );
  const openDelete = useCallback((item) => {
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'delete', item });
  }, []);
  // Escape in the document viewer must not close the form below it.
  const close = () => !busy && !viewing && setDialog(null);

  const set = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const save = async () => {
    const editing = dialog.item;
    if (!form.start) {
      setDialogError(words.missingDate);
      return;
    }
    if (form.end && form.end < form.start) {
      setDialogError('La date de fin doit suivre la date de début.');
      return;
    }
    if (types && form.type === 'Autres' && !form.comment.trim()) {
      setDialogError('Précisez le type (« Autre »).');
      return;
    }
    if (!editing && unitChoices.length && !form.unit) {
      setDialogError('Choisissez l’unité.');
      return;
    }
    if (!form.documents.length && !form.links.length && !form.pending.length) {
      setDialogError('Ajoutez au moins un document.');
      return;
    }
    setBusy(true);
    setDialogError('');
    const entryUnit = form.unit || (editing && editing.service) || user.service || '';
    let documents = form.documents;
    try {
      if (form.pending.length) {
        try {
          documents = [...documents, ...(await sendFiles(form.pending, { section: config.section, unit: entryUnit }, setSending))];
          setForm((current) => ({ ...current, documents, pending: [] }));
        } catch (sendError) {
          const sent = sendError.sent || [];
          documents = [...documents, ...sent];
          setForm((current) => ({ ...current, documents, pending: current.pending.slice(sent.length) }));
          throw sendError;
        } finally {
          setSending(null);
        }
      }
      if (form.removed.length) {
        const failed = await archiveDocuments(form.removed);
        setForm((current) => ({ ...current, removed: [] }));
        if (failed.length) {
          documents = [...documents, ...failed];
          setForm((current) => ({ ...current, documents }));
          throw new Error('archive');
        }
      }
      const title = form.title.trim();
      const record = {
        title,
        name: title || (documents[0] || form.links[0] || {}).name || words.fallbackName,
        startDate: form.start,
        endDate: form.end || form.start,
        ...(types ? { commentType: form.type, comment: form.type === 'Autres' ? form.comment.trim() : form.type } : {}),
        service: entryUnit,
        documents: documents.map(({ id, name, mimeType }) => ({ id, name, mimeType: mimeType || '' })),
        dropboxLinks: form.links.map(({ link, name }) => ({ link, name })),
      };
      const now = new Date().toISOString();
      if (editing) {
        await updateDoc(doc(db, config.collection, editing.id), { ...record, link: deleteField(), updatedBy: user.name || '', updatedAt: now });
        setNotice(words.updated);
      } else {
        // "timestamp": order of deposit, read by the dashboard.
        await addDoc(collection(db, config.collection), { ...record, createdBy: user.name || '', createdAt: now, timestamp: now });
        setNotice(words.added);
      }
      // Shown where it now stands (a former document that just got its date leaves "Sans date").
      chooseView((form.end || form.start) < todayKey() ? 'past' : 'upcoming');
      setDialog(null);
    } catch (error) {
      setDialogError(error.message === 'archive' ? 'Un document n’a pas pu être retiré. Réessayez.' : await documentError(error, words.saveFailed));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setDialogError('');
    try {
      const failed = await archiveDocuments(documentsOf(dialog.item));
      if (failed.length) {
        await updateDoc(doc(db, config.collection, dialog.item.id), { documents: failed });
        throw new Error('archive');
      }
      await deleteDoc(doc(db, config.collection, dialog.item.id));
      setDialog(null);
      setNotice(words.deleted);
    } catch (error) {
      setDialogError(error.message === 'archive' ? 'Certains documents n’ont pas pu être retirés. Réessayez.' : words.deleteFailed);
    } finally {
      setBusy(false);
    }
  };

  const editingItem = dialog && dialog.item;
  // Taking a stored document out of an entry sends it to the archives: deletion level.
  const canRemoveStored = editingItem ? access.canDelete(editingItem) : access.canAdd;
  const deletingHasDocuments = dialog && dialog.mode === 'delete' && documentsOf(dialog.item).length > 0;

  const viewChip = ({ value, label }) => {
    if (value === 'undated' && !counts.undated) return null;
    const active = view === value;
    return (
      <button
        key={value}
        type="button"
        aria-pressed={active}
        onClick={() => chooseView(value)}
        className={`inline-flex h-9 flex-none cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border-0 px-[14px] text-[13.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 ${
          active ? 'bg-ink text-white' : 'bg-white text-black/65 shadow-[0_1px_2px_rgba(0,0,0,0.06)] hover:text-ink'
        }`}
      >
        {label}
        <span className={`tabular-nums ${active ? 'text-white/60' : 'text-black/35'}`}>{counts[value]}</span>
      </button>
    );
  };

  const more = Math.min(PAGE_SIZE, shown.length - limit);

  return (
    <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        eyebrow={config.eyebrow}
        title={config.title}
        subtitle={status === 'ready' ? `${activeUnit ? `Unité ${activeUnit}` : 'Toutes les unités'} · ${counts.upcoming} ${plural(counts.upcoming)} à venir` : words.loading}
        actions={
          access.canAdd && (
            <button type="button" onClick={openAdd} className={`${BUTTON.primary} ${SIZE.md}`}>
              <FiPlus size={16} aria-hidden="true" />
              {words.add}
            </button>
          )
        }
      />

      {notice && (
        <div className="mt-6">
          <Notice tone="green">{notice}</Notice>
        </div>
      )}

      <div className="mt-8 flex flex-col gap-[12px]">
        <div className="relative">
          <FiSearch size={17} aria-hidden="true" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-black/35" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={words.searchPlaceholder}
            aria-label={words.searchLabel}
            className={`${INPUT} h-11 pl-[42px]`}
          />
        </div>
        <div className="flex flex-col gap-[12px] lg:flex-row lg:items-center lg:justify-between">
          <div className="-mx-[20px] flex gap-2 overflow-x-auto px-[20px] pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden">{VIEWS.map(viewChip)}</div>
          {unitsInData.length > 1 && (
            <Segmented value={activeUnit} onChange={setUnit} allowEmpty={false} options={[{ value: '', label: 'Toutes les unités' }, ...unitsInData.map((name) => ({ value: name, label: name }))]} />
          )}
        </div>
      </div>

      <section className={`${CARD} mt-[20px] overflow-hidden`}>
        {status === 'loading' ? (
          <div className="p-6">
            <SkeletonRows rows={5} />
          </div>
        ) : status === 'error' ? (
          <EmptyState icon={FiAlertCircle} title={words.unavailable}>
            Ils n’ont pas pu être chargés. Vérifiez la connexion, puis rechargez la page.
          </EmptyState>
        ) : shown.length === 0 ? (
          <EmptyState icon={FiCalendar} title={view === 'upcoming' ? words.emptyUpcoming : view === 'past' ? words.emptyPast : words.emptyUndated}>
            {query ? words.noMatch : view === 'upcoming' ? words.upcomingHint : 'Rien à afficher pour ce filtre.'}
          </EmptyState>
        ) : (
          <>
            {view === 'undated' && (
              <p className="m-0 border-0 border-b-[1px] border-solid border-black/[0.05] px-[16px] py-[12px] text-[13px] text-black/50 sm:px-6">
                Documents déposés avant l’ajout des dates. Indiquez leur date avec « Modifier » pour les classer.
              </p>
            )}
            <ul className="m-0 list-none p-0">
              {shown.slice(0, limit).map((entry) => (
                <ScheduleRow
                  key={entry.item.id}
                  entry={entry}
                  types={types}
                  tint={config.tint}
                  editable={access.canEdit(entry.item)}
                  deletable={access.canDelete(entry.item)}
                  onOpen={setViewing}
                  onEdit={openEdit}
                  onDelete={openDelete}
                />
              ))}
            </ul>
            {limit < shown.length && (
              <div className="flex flex-col items-center gap-1 border-0 border-t-[1px] border-solid border-black/[0.05] p-[16px] text-center">
                <button type="button" onClick={() => setLimit((count) => count + PAGE_SIZE)} className={`${BUTTON.ghost} ${SIZE.md}`}>
                  Afficher {more} {plural(more)} de plus
                </button>
                <span className="text-[12.5px] text-black/40">
                  {limit} sur {shown.length} affichés
                </span>
              </div>
            )}
          </>
        )}
      </section>

      <Modal
        open={Boolean(dialog && dialog.mode === 'form')}
        title={editingItem ? words.editTitle : words.add}
        onClose={close}
        size="lg"
        footer={
          <>
            <button type="button" onClick={close} disabled={busy} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={save} disabled={busy} className={`${BUTTON.primary} ${SIZE.md}`}>
              {sending ? `Envoi ${sending.index + 1}/${form.pending.length || sending.index + 1}…` : busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-[18px]">
          <Field id="schedule-title" label="Intitulé" hint={words.titleHint}>
            <TextInput id="schedule-title" value={form.title} onChange={(e) => set('title', e.target.value)} />
          </Field>
          <div className="grid grid-cols-1 gap-[16px] sm:grid-cols-2">
            <Field id="schedule-start" label="Du" required>
              <TextInput
                id="schedule-start"
                type="date"
                value={form.start}
                onChange={(e) => setForm((current) => ({ ...current, start: e.target.value, end: !current.end || current.end < e.target.value ? e.target.value : current.end }))}
              />
            </Field>
            <Field id="schedule-end" label="Au">
              <TextInput id="schedule-end" type="date" min={form.start || undefined} value={form.end} onChange={(e) => set('end', e.target.value)} />
            </Field>
          </div>
          {types && (
            <Field id="schedule-type" label="Type">
              <Segmented id="schedule-type" value={form.type} onChange={(value) => set('type', value)} options={types.options} allowEmpty={false} />
            </Field>
          )}
          {types && form.type === 'Autres' && (
            <Field id="schedule-comment" label="Précisez" required>
              <TextInput id="schedule-comment" value={form.comment} onChange={(e) => set('comment', e.target.value)} placeholder={types.otherPlaceholder} />
            </Field>
          )}
          {unitChoices.length > 0 && (
            <Field id="schedule-unit" label="Unité" required={!editingItem}>
              <Select
                id="schedule-unit"
                value={form.unit}
                onChange={(e) => set('unit', e.target.value)}
                placeholder="Choisir l’unité…"
                options={form.unit && !unitChoices.includes(form.unit) ? [form.unit, ...unitChoices] : unitChoices}
              />
            </Field>
          )}
          <Field id="schedule-documents" label="Documents" required>
            <AttachmentsField
              id="schedule-documents"
              status={documentsStatus}
              stored={form.documents}
              onOpen={(document) => setViewing({ document, item: editingItem })}
              onRemoveStored={
                canRemoveStored && !busy
                  ? (item) => setForm((current) => ({ ...current, documents: current.documents.filter((entry) => entry.id !== item.id), removed: [...current.removed, item] }))
                  : null
              }
              links={form.links}
              onLinksChange={busy ? null : (links) => set('links', links)}
              pending={form.pending}
              onPendingChange={(pending) => {
                set('pending', pending);
                setDialogError('');
              }}
              sending={sending}
            />
          </Field>
          {form.removed.length > 0 && (
            <p className="m-0 text-[12.5px] text-black/45">
              {form.removed.length === 1 ? 'Un document sera retiré' : `${form.removed.length} documents seront retirés`} à l’enregistrement (conservés dans les archives du
              Dropbox du service).
            </p>
          )}
          {dialogError && <Notice tone="red">{dialogError}</Notice>}
        </div>
      </Modal>

      <Modal
        open={Boolean(dialog && dialog.mode === 'delete')}
        title={words.deleteTitle}
        onClose={close}
        footer={
          <>
            <button type="button" onClick={close} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={remove} disabled={busy} className={`${BUTTON.dangerSolid} ${SIZE.md}`}>
              {busy ? 'Suppression…' : 'Supprimer'}
            </button>
          </>
        }
      >
        <p className="m-0 text-[14.5px] leading-relaxed text-black/55">
          <strong className="text-ink">{dialog && dialog.item ? titleOf(dialog.item, words.fallbackName) : ''}</strong> (
          {dialog && dialog.item ? periodLabel(periodOf(dialog.item)).toLowerCase() : ''}) sera supprimé.{' '}
          {deletingHasDocuments ? 'Ses documents restent conservés dans les archives du Dropbox du service.' : 'Les fichiers restent dans Dropbox.'}
        </p>
        {dialogError && (
          <div className="mt-[12px]">
            <Notice tone="red">{dialogError}</Notice>
          </div>
        )}
      </Modal>

      {viewing && (
        <DocumentViewer
          item={viewing.document}
          onClose={() => setViewing(null)}
          canReplace={Boolean(viewing.item) && access.canEdit(viewing.item)}
          canDownload={access.canDownload}
        />
      )}
    </main>
  );
};

export default SchedulePage;
